/**
 * AccountCategories Component
 * 
 * Displays accounts grouped by type in collapsible categories.
 * 
 * Features:
 * - Collapsible account type sections
 * - Account cards with proper fields
 * - Add Transaction(s) links
 * - Edit/Delete actions
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Account, AccountType } from '../../types/account.types';
import { calculateTotalInflow, calculateTotalOutflow } from '../../utils/balance-calculator';
import type { Transaction } from '../../types/transaction.types';
import { Wallet, PiggyBank, CreditCard, TrendingUp, Building2, Home, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { useRegion } from '../../contexts/RegionContext';
import 'remixicon/fonts/remixicon.css';

interface AccountCategoriesProps {
  accounts: (Account & { _transactions?: Transaction[] })[];
  loading: boolean;
  onEdit: (account: Account) => void;
  onDelete: (accountId: string) => void;
  onClose: (accountId: string, closureDate: string, closureReason?: string) => void;
  onAddAccount?: (type: AccountType) => void;
  onImport?: (accountId: string) => void;
}

interface AccountCardProps {
  account: Account & { _transactions?: Transaction[] };
  onEdit: () => void;
  onDelete: () => void;
  onClose: (closureDate: string, closureReason?: string) => void;
  onImport?: (accountId: string) => void;
}

function AccountCard({ account, onEdit, onDelete: _onDelete, onClose, onImport }: AccountCardProps): React.ReactElement {
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [closureDate, setClosureDate] = useState('');
  const [closureReason, setClosureReason] = useState('');
  const { formatDate } = useRegion();

  const getAccountIcon = (type: AccountType) => {
    switch (type) {
      case AccountType.BANK_CASH:
        return Wallet;
      case AccountType.CREDIT_CARD:
        return CreditCard;
      case AccountType.SAVINGS:
        return PiggyBank;
      case AccountType.LOAN:
        return TrendingUp;
      case AccountType.INVESTMENTS_SUPERANNUATION:
        return Building2;
      case AccountType.ASSETS:
        return Home;
      default:
        return Wallet;
    }
  };

  const getAccountIconColor = (type: AccountType) => {
    switch (type) {
      case AccountType.BANK_CASH:
        return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
      case AccountType.CREDIT_CARD:
        return 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400';
      case AccountType.SAVINGS:
        return 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400';
      case AccountType.LOAN:
        return 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400';
      case AccountType.INVESTMENTS_SUPERANNUATION:
        return 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400';
      case AccountType.ASSETS:
        return 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getAccountTypeLabel = (type: AccountType) => {
    switch (type) {
      case AccountType.BANK_CASH:
        return 'Everyday';
      case AccountType.CREDIT_CARD:
        return 'Liabilities';
      case AccountType.SAVINGS:
        return 'Savings';
      case AccountType.LOAN:
        return 'Liabilities';
      case AccountType.INVESTMENTS_SUPERANNUATION:
        return 'Investments';
      case AccountType.ASSETS:
        return 'Assets';
      default:
        return type;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };


  const handleCloseSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (closureDate) {
      onClose(closureDate, closureReason || undefined);
      setShowCloseForm(false);
      setClosureDate('');
      setClosureReason('');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      {/* Header with Icon and Title */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getAccountIconColor(account.type)}`}>
            {React.createElement(getAccountIcon(account.type), { className: 'h-5 w-5' })}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-base">
              {account.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {getAccountTypeLabel(account.type)}
            </p>
          </div>
        </div>
      </div>

      {/* Current Balance/Value - Large and Prominent */}
      <div className="mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
          {account.type === AccountType.ASSETS ? 'Current Value' : 'Current Balance'}
        </p>
        <p className={`text-2xl font-bold ${
          (account.type === AccountType.ASSETS ? (account.currentValue || 0) : account.currentBalance) >= 0 
            ? 'text-gray-900 dark:text-gray-100' 
            : 'text-red-600 dark:text-red-400'
        }`}>
          {formatCurrency(
            account.type === AccountType.ASSETS 
              ? (account.currentValue || 0)
              : account.currentBalance
          )}
        </p>
      </div>

      {/* Account Details Grid */}
      {account.type === AccountType.ASSETS ? (
        // Asset-specific fields grid
        <div className="space-y-3 mb-4">
          {/* Row 1: Category | Subcategory (3 columns to align with row below) */}
          <div className="grid grid-cols-3 gap-3">
            {account.category && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Category</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {account.category.charAt(0).toUpperCase() + account.category.slice(1).replace(/_/g, ' ')}
                </p>
              </div>
            )}

            {account.subType && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Subcategory</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {account.subType.charAt(0).toUpperCase() + account.subType.slice(1).replace(/_/g, ' ')}
                </p>
              </div>
            )}
          </div>

          {/* Row 2: Purchase Price | Purchase Date | Supplier */}
          <div className="grid grid-cols-3 gap-3">
            {account.purchasePrice !== undefined && account.purchasePrice !== null ? (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Purchase Price</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatCurrency(account.purchasePrice)}
                </p>
              </div>
            ) : (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Purchase Price</p>
                <p className="text-sm font-medium text-gray-400 dark:text-gray-500">
                  —
                </p>
              </div>
            )}

            {account.purchaseDate ? (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Purchase Date</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatDate(account.purchaseDate)}
                </p>
              </div>
            ) : (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Purchase Date</p>
                <p className="text-sm font-medium text-gray-400 dark:text-gray-500">
                  —
                </p>
              </div>
            )}

            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Supplier</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {account.provider}
              </p>
            </div>
          </div>
        </div>
      ) : (
        // Transaction-related fields for non-asset accounts
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">In Flow</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {formatCurrency(calculateTotalInflow(account._transactions || [], account.id, account.openingDate || account.createdAt))}
            </p>
          </div>
          
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Out Flow</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {formatCurrency(calculateTotalOutflow(account._transactions || [], account.id, account.openingDate || account.createdAt))}
            </p>
          </div>
          
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1"># Trans.</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {account._transactions?.length || 0}
            </p>
          </div>

          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Starting Balance</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {formatCurrency(account.openingBalance)}
            </p>
          </div>

          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Provider</p>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {account.provider}
            </p>
          </div>

          {/* Type-specific fields */}
          {account.type === AccountType.CREDIT_CARD && account.creditLimit && (
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Credit Limit</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatCurrency(account.creditLimit)}
              </p>
            </div>
          )}

          {account.type === AccountType.LOAN && account.loanAmount && (
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Original Loan Amount</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatCurrency(account.loanAmount)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {account.type !== AccountType.ASSETS && (
            <>
              {onImport ? (
                <button
                  onClick={() => onImport(account.id)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-2 py-1"
                  aria-label={`Add transaction(s) to ${account.name}`}
                >
                  Add Transaction(s)
                </button>
              ) : (
                <span className="text-sm text-gray-400 dark:text-gray-500 font-medium">
                  Add Transaction(s)
                </span>
              )}
              <Link
                to={`/transactions?accountId=${account.id}`}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded px-2 py-1 flex items-center gap-1 transition-colors"
                aria-label={`View transactions for ${account.name}`}
              >
                <span>View Transactions</span>
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {account.type === AccountType.ASSETS && account.isActive && (
            <button
              type="button"
              onClick={() => {
                setClosureDate(new Date().toISOString().split('T')[0]);
                setClosureReason('Sold');
                setShowCloseForm(true);
              }}
              className="text-sm text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 rounded px-2 py-1"
              aria-label={`Mark ${account.name} as sold`}
            >
              Mark as Sold
            </button>
          )}
          {account.type !== AccountType.ASSETS && account.isActive && (
            <button
              type="button"
              onClick={() => setShowCloseForm(!showCloseForm)}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded px-2 py-1"
              aria-label={`Close ${account.name}`}
            >
              Close
            </button>
          )}
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={`Edit ${account.name}`}
          >
            <i className="ri-edit-line text-lg"></i>
          </button>
        </div>
      </div>

      {/* Close / Mark as Sold Form */}
      {showCloseForm && (
        <form onSubmit={handleCloseSubmit} className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="space-y-3">
            <div>
              <label htmlFor={`closureDate-${account.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {account.type === AccountType.ASSETS ? 'Sale Date' : 'Closure Date'}
              </label>
              <input
                type="date"
                id={`closureDate-${account.id}`}
                value={closureDate}
                onChange={(e) => setClosureDate(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            <div>
              <label htmlFor={`closureReason-${account.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Reason (Optional)
              </label>
              <textarea
                id={`closureReason-${account.id}`}
                value={closureReason}
                onChange={(e) => setClosureReason(e.target.value)}
                rows={2}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                placeholder={
                  account.type === AccountType.ASSETS
                    ? 'e.g. Sold privately, trade-in…'
                    : 'Reason for closing this account...'
                }
              />
            </div>
            <div className="flex space-x-2">
              <button
                type="submit"
                className="px-3 py-1 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
              >
                {account.type === AccountType.ASSETS ? 'Mark as Sold' : 'Close Account'}
              </button>
              <button
                type="button"
                onClick={() => setShowCloseForm(false)}
                className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {!account.isActive && (
        <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Closed on {account.closureDate ? formatDate(account.closureDate) : 'Unknown date'}
          </p>
        </div>
      )}
    </div>
  );
}

export function AccountCategories({ 
  accounts, 
  loading, 
  onEdit, 
  onDelete, 
  onClose,
  onAddAccount,
  onImport
}: AccountCategoriesProps): React.ReactElement {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['everyday', 'liabilities', 'savings', 'investments', 'assets']));

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-12">
          <div className="flex items-center justify-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
              <p className="text-lg text-gray-600 dark:text-gray-400 font-medium">Loading accounts...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-12">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 text-gray-400 mb-4">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No accounts found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Get started by adding your first account to track your finances.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Group accounts by type
  const groupedAccounts = {
    everyday: accounts.filter(acc => acc.type === AccountType.BANK_CASH),
    liabilities: accounts.filter(acc => acc.type === AccountType.CREDIT_CARD || acc.type === AccountType.LOAN),
    savings: accounts.filter(acc => acc.type === AccountType.SAVINGS),
    investments: accounts.filter(acc => acc.type === AccountType.INVESTMENTS_SUPERANNUATION),
    assets: accounts.filter(acc => acc.type === AccountType.ASSETS),
  };

  const categoryConfig = [
    {
      key: 'everyday',
      title: 'Everyday Accounts',
      icon: Wallet,
      color: 'blue',
      accounts: groupedAccounts.everyday,
      accountType: AccountType.BANK_CASH,
    },
    {
      key: 'liabilities',
      title: 'Liabilities',
      icon: CreditCard,
      color: 'orange',
      accounts: groupedAccounts.liabilities,
      accountType: AccountType.CREDIT_CARD, // Default to credit card, but could be loan too
    },
    {
      key: 'savings',
      title: 'Savings',
      icon: PiggyBank,
      color: 'green',
      accounts: groupedAccounts.savings,
      accountType: AccountType.SAVINGS,
    },
    {
      key: 'investments',
      title: 'Investments',
      icon: Building2,
      color: 'purple',
      accounts: groupedAccounts.investments,
      accountType: AccountType.INVESTMENTS_SUPERANNUATION,
    },
    {
      key: 'assets',
      title: 'Assets',
      icon: Home,
      color: 'indigo',
      accounts: groupedAccounts.assets,
      accountType: AccountType.ASSETS,
    },
  ];

  return (
    <div className="space-y-6">
      {categoryConfig.map((category) => {
        if (category.accounts.length === 0) return null;
        
        const isExpanded = expandedCategories.has(category.key);
        
        return (
          <div key={category.key} id={`category-${category.key}`} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 scroll-mt-24">
            <div className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200">
              <div className="flex items-center space-x-3 flex-1">
                <button
                  onClick={() => toggleCategory(category.key)}
                  className="flex items-center space-x-3 flex-1 text-left"
                >
                  {React.createElement(category.icon, { className: 'h-5 w-5 text-gray-600 dark:text-gray-400' })}
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {category.title}
                  </h3>
                  <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                    {category.accounts.length}
                  </span>
                </button>
                {onAddAccount && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddAccount(category.accountType);
                    }}
                    className="ml-2 px-2 py-1 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white rounded-md transition-colors duration-200 flex items-center justify-center"
                    aria-label={`Add new ${category.title.toLowerCase()}`}
                    title={`Add new ${category.title.toLowerCase()}`}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                )}
              </div>
              <button
                onClick={() => toggleCategory(category.key)}
                className="ml-2"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            
            {isExpanded && (
              <div className="px-6 pb-6">
                <div className="main-page-grid-2">
                  {category.accounts.map((account) => (
                    <AccountCard
                      key={account.id}
                      account={account}
                      onEdit={() => onEdit(account)}
                      onDelete={() => onDelete(account.id)}
                      onClose={(closureDate, closureReason) => onClose(account.id, closureDate, closureReason)}
                      onImport={onImport ? () => onImport(account.id) : undefined}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
